import { useState } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Warehouse, Factory, Car, Building2, Wrench, Layers, Ruler, Thermometer, ShieldCheck, AlertTriangle, CheckCircle2, Sparkles, Clock, Calendar, MapPin, ArrowRight, ArrowLeft, Loader2, Send, Pencil, HelpCircle } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Overline } from "./Reveal";

const AREA_TYPES = [
  { id: "csaladi", label: "Családi ház", icon: Home },
  { id: "eloteto", label: "Előtető", icon: Warehouse },
  { id: "ipari", label: "Ipari épület", icon: Factory },
  { id: "kocsibeallo", label: "Kocsibeálló", icon: Car },
  { id: "egyeb", label: "Egyéb…", icon: Building2 },
];
const WORK_TYPES = [
  { id: "tetofelujitas", label: "Tetőfelújítás", icon: Wrench },
  { id: "uj-tetofedes", label: "Új tetőfedés", icon: Layers },
  { id: "hoszigeteles", label: "Hőszigetelés", icon: Thermometer },
  { id: "badogos", label: "Bádogos munkák", icon: Ruler },
  { id: "tanacs", label: "Nem tudom / kérek tanácsot", icon: HelpCircle },
];
const SIZE_RANGES = [
  { id: "s", label: "50 m² alatt" },
  { id: "m", label: "50–100 m²" },
  { id: "l", label: "100–200 m²" },
  { id: "xl", label: "200 m² felett" },
  { id: "unknown", label: "Nem tudom pontosan" },
];
const CONDITIONS = [
  { id: "beazas", label: "Beázik / sürgős", icon: AlertTriangle },
  { id: "felujitasra", label: "Felújításra szorul", icon: Wrench },
  { id: "jo", label: "Jó állapotú, karbantartás", icon: ShieldCheck },
  { id: "uj-epites", label: "Új építés", icon: Sparkles },
];
const TIMINGS = [
  { id: "minal-elobb", label: "Minél előbb", icon: Clock },
  { id: "1-honap", label: "1 hónapon belül", icon: Calendar },
  { id: "3-honap", label: "1–3 hónapon belül", icon: Calendar },
  { id: "csak-arajanlat", label: "Csak árajánlat érdekel", icon: HelpCircle },
];
const STEP_TITLES = ["Munkaterület", "Munka", "Méret", "Állapot", "Kezdés", "Helység", "Adatok"];
const EMPTY = { areaType: "", workType: "", sizeRange: "", condition: "", timing: "", town: "", name: "", phone: "", email: "", consent: false };

const OptionCard = ({ active, onClick, testid, icon: Icon, label }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`group relative flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition-all ${
      active ? "border-emerald-500 bg-emerald-50 shadow-lg" : "border-slate-200 bg-white hover:border-emerald-300 hover:-translate-y-0.5 hover:shadow-md"
    }`}
  >
    {Icon && (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
        <Icon className="h-5 w-5" />
      </span>
    )}
    <span className="text-sm font-semibold text-[#0E5A75]">{label}</span>
    {active && <CheckCircle2 className="absolute right-2.5 top-2.5 h-5 w-5 text-emerald-500" />}
  </button>
);

export default function Calculator({ id = "ajanlatkeres" }) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sendError, setSendError] = useState("");

  const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const go = (to) => { setDir(to > step ? 1 : -1); setStep(to); window.__lenis && window.__lenis.scrollTo(document.getElementById(id), { offset: -10, duration: 0.6 }); };
  const pick = (k, v) => { set(k, v); setTimeout(() => go(step + 1), 180); };

  const validateStep = () => {
    const e = {};
    if (step === 5 && !data.town.trim()) e.town = "Kérjük, adja meg a helységet vagy irányítószámot.";
    if (step === 6) {
      if (!data.name.trim()) e.name = "Kérjük, adja meg a nevét.";
      if (!data.phone.trim() || !/^[+0-9 ()/-]{6,}$/.test(data.phone.trim())) e.phone = "Adjon meg érvényes telefonszámot.";
      if (data.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) e.email = "Adjon meg érvényes e-mail-címet.";
      if (!data.consent) e.consent = "Az adatkezelési hozzájárulás kötelező.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) go(step + 1); };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true); setSendError("");
    try {
      const lab = (arr, v) => arr.find((x) => x.id === v)?.label || v || "—";
      const params = {
        to_email: process.env.REACT_APP_NOTIFICATION_EMAIL || "grudabau@gmail.com",
        name: data.name, phone: data.phone, email: data.email || "(nem adott meg)", town: data.town,
        areaType: lab(AREA_TYPES, data.areaType),
        workType: lab(WORK_TYPES, data.workType),
        sizeRange: lab(SIZE_RANGES, data.sizeRange),
        condition: lab(CONDITIONS, data.condition),
        timing: lab(TIMINGS, data.timing),
        submittedAt: new Date().toLocaleString("hu-HU"),
        source: "Weboldal – Árkalkulátor",
      };
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        params,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Árkalkulátor", content_category: params.workType });
      }
      setDone(true);
    } catch (err) {
      console.error("EmailJS send failed:", err);
      setSendError("Az elküldés nem sikerült. Kérjük, próbálja újra, vagy hívjon minket telefonon.");
    } finally { setSubmitting(false); }
  };

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 40 : -40 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -40 : 40 }),
  };

  const lab = (arr, v) => arr.find((x) => x.id === v)?.label;
  const summaryRows = [
    ["Munkaterület", lab(AREA_TYPES, data.areaType), 0],
    ["Munka", lab(WORK_TYPES, data.workType), 1],
    ["Méret", lab(SIZE_RANGES, data.sizeRange), 2],
    ["Állapot", lab(CONDITIONS, data.condition), 3],
    ["Kezdés", lab(TIMINGS, data.timing), 4],
    ["Helység", data.town, 5],
  ].filter(([, v]) => v);

  const renderChoice = (list, key) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {list.map((o) => (
        <OptionCard key={o.id} testid={`calc-${key}-${o.id}`} icon={o.icon} label={o.label} active={data[key] === o.id} onClick={() => pick(key, o.id)} />
      ))}
    </div>
  );

  return (
    <section id={id} data-testid="quote-section" className="scroll-mt-20 relative overflow-hidden bg-slate-50 py-16 sm:py-20 md:py-32">
      <div className="pointer-events-none absolute -top-24 right-0 h-[420px] w-[420px] rounded-full bg-[#38A8CE]/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-20 h-[360px] w-[360px] rounded-full bg-emerald-400/15 blur-3xl" />

      <div className="relative mx-auto max-w-4xl px-5 md:px-10">
        <div className="mx-auto max-w-2xl text-center">
          <div className="flex justify-center"><Overline>Gyors árajánlat</Overline></div>
          <h2 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-gradient-brand text-balance">Kérjen ingyenes szakmai felmérést</h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">Néhány kérdés, és hamarosan felvesszük Önnel a kapcsolatot személyre szabott árajánlattal.</p>
        </div>

        <div className="mt-12 rounded-[2rem] border border-slate-200 bg-white p-5 sm:p-9 shadow-2xl shadow-slate-300/40">
          {!done && (
            <div className="mb-8" data-testid="calc-progress">
              <div className="mb-3 flex items-center justify-between text-xs sm:text-sm font-semibold text-slate-500">
                <span>Lépés {step + 1} / {STEP_TITLES.length}</span>
                <span>{Math.round(((step + 1) / STEP_TITLES.length) * 100)}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-gradient-to-r from-[#38A8CE] to-emerald-500 transition-all duration-500" style={{ width: `${((step + 1) / STEP_TITLES.length) * 100}%` }} />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait" custom={dir}>
            {done ? (
              <motion.div key="done" data-testid="quote-success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center text-center py-8">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></span>
                <h3 className="mt-6 text-2xl font-bold text-[#0E5A75]">Köszönjük! Megkaptuk az adatokat.</h3>
                <p className="mt-3 max-w-md text-slate-600 leading-relaxed">Munkatársunk hamarosan felveszi Önnel a kapcsolatot személyre szabott árajánlattal.</p>
                <button data-testid="quote-reset" onClick={() => { setData(EMPTY); setStep(0); setDone(false); }} className="mt-8 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-[#0E5A75] hover:border-emerald-500 hover:text-emerald-600 transition-colors">Új ajánlatkérés</button>
              </motion.div>
            ) : (
              <motion.div key={step} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: "easeOut" }}>
                {step === 0 && (<><h3 className="mb-6 text-xl font-bold text-[#0E5A75]">Melyik leírás illik legjobban a munkaterülethez?</h3>{renderChoice(AREA_TYPES, "areaType")}</>)}
                {step === 1 && (<><h3 className="mb-6 text-xl font-bold text-[#0E5A75]">Milyen munkára van szüksége?</h3>{renderChoice(WORK_TYPES, "workType")}</>)}
                {step === 2 && (<><h3 className="mb-6 text-xl font-bold text-[#0E5A75]">Mekkora a tető becsült mérete?</h3>{renderChoice(SIZE_RANGES, "sizeRange")}</>)}
                {step === 3 && (<><h3 className="mb-6 text-xl font-bold text-[#0E5A75]">Milyen állapotban van a tető?</h3>{renderChoice(CONDITIONS, "condition")}</>)}
                {step === 4 && (<><h3 className="mb-6 text-xl font-bold text-[#0E5A75]">Mikorra tervezné a munkát?</h3>{renderChoice(TIMINGS, "timing")}</>)}
                {step === 5 && (
                  <>
                    <h3 className="mb-6 text-xl font-bold text-[#0E5A75]">Melyik településen található az ingatlan?</h3>
                    <Label htmlFor="calc-town" className="text-slate-600">Helység vagy irányítószám</Label>
                    <div className="relative mt-2">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500" />
                      <Input id="calc-town" data-testid="quote-town" value={data.town} onChange={(e) => set("town", e.target.value)} placeholder="Pl. Győr vagy 9022" className={`h-14 rounded-xl bg-slate-50 pl-12 text-lg ${errors.town ? "border-red-400" : "border-slate-200"}`} />
                    </div>
                    {errors.town && <p data-testid="error-town" className="mt-2 text-sm text-red-500">{errors.town}</p>}
                  </>
                )}
                {step === 6 && (
                  <>
                    <h3 className="mb-6 text-xl font-bold text-[#0E5A75]">Hová küldhetjük az ajánlatot?</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="calc-name" className="text-[#0E5A75]">Név <span className="text-emerald-600">*</span></Label>
                        <Input id="calc-name" data-testid="quote-name" value={data.name} onChange={(e) => set("name", e.target.value)} className={`mt-2 h-12 rounded-xl bg-slate-50 ${errors.name ? "border-red-400" : "border-slate-200"}`} placeholder="Teljes név" />
                        {errors.name && <p data-testid="error-name" className="mt-1 text-xs text-red-500">{errors.name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="calc-phone" className="text-[#0E5A75]">Telefonszám <span className="text-emerald-600">*</span></Label>
                        <Input id="calc-phone" data-testid="quote-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} className={`mt-2 h-12 rounded-xl bg-slate-50 ${errors.phone ? "border-red-400" : "border-slate-200"}`} placeholder="+36 ..." />
                        {errors.phone && <p data-testid="error-phone" className="mt-1 text-xs text-red-500">{errors.phone}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="calc-email" className="text-[#0E5A75]">E-mail <span className="text-slate-400 text-xs">(opcionális)</span></Label>
                        <Input id="calc-email" data-testid="quote-email" value={data.email} onChange={(e) => set("email", e.target.value)} className={`mt-2 h-12 rounded-xl bg-slate-50 ${errors.email ? "border-red-400" : "border-slate-200"}`} placeholder="pelda@email.hu" />
                        {errors.email && <p data-testid="error-email" className="mt-1 text-xs text-red-500">{errors.email}</p>}
                      </div>
                      <div className="sm:col-span-2 flex items-start gap-3">
                        <Checkbox id="calc-consent" data-testid="quote-consent" checked={data.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" />
                        <Label htmlFor="calc-consent" className="text-sm font-normal text-slate-600 leading-relaxed cursor-pointer">Hozzájárulok, hogy a megadott adataimat a Tetőklinika az ajánlatkérés feldolgozása céljából kezelje. <span className="text-emerald-600">*</span></Label>
                      </div>
                      {errors.consent && <p data-testid="error-consent" className="-mt-2 text-xs text-red-500 sm:col-span-2">{errors.consent}</p>}
                    </div>

                    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                      <div className="mb-2 text-xs uppercase tracking-wide text-slate-400">Összegzés</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        {summaryRows.map(([k, v, toStep], idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2">
                            <span className="text-slate-500">{k}:</span>
                            <span className="flex items-center gap-2 truncate font-semibold text-[#0E5A75]">
                              {v}
                              <button type="button" data-testid={`calc-edit-${toStep}`} onClick={() => go(toStep)} className="text-emerald-600 hover:text-emerald-700"><Pencil className="h-3.5 w-3.5" /></button>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {sendError && (<div data-testid="calc-send-error" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{sendError}</div>)}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {!done && (
            <div className="mt-8 flex items-center justify-between">
              <button type="button" data-testid="calc-back" onClick={() => go(Math.max(0, step - 1))} disabled={step === 0} className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${step === 0 ? "invisible" : "text-[#0E5A75] hover:bg-slate-100"}`}>
                <ArrowLeft className="h-4 w-4" /> Vissza
              </button>
              {step === 6 ? (
                <button type="button" data-testid="quote-submit" onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-7 py-3.5 text-base font-bold text-[#0E5A75] shadow-lg shadow-emerald-400/40 hover:bg-emerald-300 hover:scale-[1.02] transition-transform disabled:opacity-70">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />} Ajánlatot kérek
                </button>
              ) : step === 5 ? (
                <button type="button" data-testid="calc-next" onClick={next} className="inline-flex items-center gap-2 rounded-full bg-[#0E5A75] px-7 py-3.5 text-base font-semibold text-white hover:bg-[#0B4A60] hover:scale-[1.02] transition-transform">
                  Tovább <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <span className="text-sm text-slate-400">Kattintson egy lehetőségre a továbblépéshez</span>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
